import { Fragment, useMemo } from 'react';

import type { GetLessonTeachingAndLearningSequenceQuery } from './teaching-notes.graphql.generated';
import { Box } from 'approot/shared/design-system/box/box.style';
import { DropdownSecondaryKebab } from 'approot/shared/dropdown/dropdown-kebab';
import { DropdownItem } from 'approot/shared/dropdown/dropdown-item.style';
import {
  LEARNING_SEQUENCE_INTRODUCTION_CATEGORIES_LB,
  LEARNING_SEQUENCE_QUESTION_CATEGORIES_AB,
  LEARNING_SEQUENCE_CONCLUSION_CATEGORIES,
  LearningSequenceItemCategories,
  LEARNING_SEQUENCE_CATEGORY_MATERIALS_NEEDED,
  LEARNING_SEQUENCE_CATEGORY_EXTRA_CONNECTIONS,
  LESSON_ACTIVITIES,
  ASSESSMENT_ACTIVITIES,
} from 'global/constants';
import * as Styled from 'approot/shared/teaching-learning-sequence/teaching-learning-sequence.style';
import TeachingNotesDetailRichtextEdit from './teaching-notes-detail-richtext-edit';
import { DIRECTION_LEFT, DIRECTION_RIGHT } from 'webclient.constants';
import { getSortByDirection } from 'lib/between/between';
import TeachingNotesMenu from './teaching-notes-menu';
import { PStyled } from 'approot/shared/styles/typography.style';
import LessonMaterialsEdit from './lesson-materials/lesson-materials-edit';
import ExtraConnectionsEdit from './extra-connections/extra-connections-edit';
import IconTrash from 'approot/shared/icons/icon-trash';
import IconArrowUp from 'approot/shared/icons/icon-arrow-up';
import IconArrowDown from 'approot/shared/icons/icon-arrow-down';
import type { Pluck } from 'lib/gql/pluck';

type Props = {
  onDelete: (
    note: Pluck<
      GetLessonTeachingAndLearningSequenceQuery,
      'getLessonTeachingAndLearningSequence'
    >
  ) => void;
  onAdd: (category: LearningSequenceItemCategories) => void;
  onChange: (
    note: Pluck<
      GetLessonTeachingAndLearningSequenceQuery,
      'getLessonTeachingAndLearningSequence'
    >
  ) => void;
  categories:
    | typeof LEARNING_SEQUENCE_INTRODUCTION_CATEGORIES_LB
    | typeof LEARNING_SEQUENCE_QUESTION_CATEGORIES_AB
    | typeof LEARNING_SEQUENCE_CONCLUSION_CATEGORIES;
  labelCategories?:
    | typeof LEARNING_SEQUENCE_INTRODUCTION_CATEGORIES_LB
    | typeof LEARNING_SEQUENCE_QUESTION_CATEGORIES_AB
    | typeof LEARNING_SEQUENCE_CONCLUSION_CATEGORIES;
  notes?: Pluck<
    GetLessonTeachingAndLearningSequenceQuery,
    'getLessonTeachingAndLearningSequence'
  >[];
  emptyState?: {
    contentType: typeof LESSON_ACTIVITIES | typeof ASSESSMENT_ACTIVITIES;
    categoryType: 'introduction' | 'conclusion';
  };
};

const TeachingNotesListEdit = ({
  notes,
  onAdd,
  onChange,
  onDelete,
  categories,
  labelCategories,
  emptyState,
}: Props) => {
  const availableCategories = useMemo(() => {
    const addedCategories = new Set(notes?.map(n => n.category) ?? []);
    return categories.filter(c => !addedCategories.has(c.value));
  }, [categories, notes]);

  return (
    <Box $marginBottom={24}>
      {notes?.length === 0 && emptyState && (
        <>
          <Styled.TeachingLearningSequenceEmptySubHeading>
            Welcome to Your{' '}
            {emptyState.contentType === LESSON_ACTIVITIES
              ? 'Lesson'
              : 'Assessment'}{' '}
            {emptyState.categoryType === 'introduction'
              ? 'Introduction'
              : 'Conclusion'}
            !
          </Styled.TeachingLearningSequenceEmptySubHeading>
          <Styled.TeachingLearningSequenceEmptyText>
            {emptyState.categoryType === 'introduction'
              ? `Choose from sections like Hook, Introduction, Learning Targets, and more to introduce this ${
                  emptyState.contentType === LESSON_ACTIVITIES
                    ? 'lesson'
                    : 'assessment'
                }. Get started by clicking 'Add'.`
              : `Finish your ${
                  emptyState.contentType === LESSON_ACTIVITIES
                    ? 'lesson'
                    : 'assessment'
                } with elements such as Assessment, Extension, Metacognition, and more. Start by clicking 'Add'.`}
          </Styled.TeachingLearningSequenceEmptyText>
        </>
      )}

      {notes?.map(note => {
        const currentIndex = notes.findIndex(n => n.id === note.id);
        const canMoveUp = currentIndex > 0;
        const canMoveDown = currentIndex < notes.length - 1;
        const isLessonMaterials =
          note.category === LEARNING_SEQUENCE_CATEGORY_MATERIALS_NEEDED;
        const isExtraConnections =
          note.category === LEARNING_SEQUENCE_CATEGORY_EXTRA_CONNECTIONS;
        const label =
          (labelCategories || categories).find(c => c.value === note.category)
            ?.label || 'Note';

        return (
          <Fragment key={note.id}>
            <Styled.TeachingLearningSequenceListDropdownWrapper data-selector="teaching-note-category-title">
              <PStyled data-selector="tls-section-title-edit" marginBottom={0}>
                {label}
              </PStyled>

              <Styled.TeachingLearningSequenceListActionsWrapper>
                <TeachingNotesMenu
                  options={[
                    ...(canMoveUp
                      ? [
                          {
                            onClick: () =>
                              onChange({
                                ...note,
                                sort: getSortByDirection(
                                  notes,
                                  currentIndex,
                                  DIRECTION_LEFT
                                ),
                              }),
                            disabled: currentIndex === 0,
                            ariaLabel: 'Move this note up',
                            icon: <IconArrowUp width={18} height={18} />,
                            dataSelector: 'move-up-button',
                          },
                        ]
                      : []),
                    ...(canMoveDown
                      ? [
                          {
                            onClick: () =>
                              onChange({
                                ...note,
                                sort: getSortByDirection(
                                  notes,
                                  currentIndex,
                                  DIRECTION_RIGHT
                                ),
                              }),
                            disabled: currentIndex === notes.length - 1,
                            ariaLabel: 'Move this note down',
                            icon: <IconArrowDown width={18} height={18} />,
                            dataSelector: 'move-down-button',
                          },
                        ]
                      : []),
                    {
                      onClick: () => onDelete(note),
                      ariaLabel: 'Delete this note',
                      icon: <IconTrash width={18} height={18} />,
                      dataSelector: 'delete-button',
                    },
                  ]}
                />
              </Styled.TeachingLearningSequenceListActionsWrapper>
            </Styled.TeachingLearningSequenceListDropdownWrapper>

            {isLessonMaterials ? (
              <LessonMaterialsEdit note={note} onChange={onChange} />
            ) : isExtraConnections ? (
              <ExtraConnectionsEdit note={note} onChange={onChange} />
            ) : (
              <TeachingNotesDetailRichtextEdit
                key={note.id}
                onChange={json =>
                  onChange({
                    ...note,
                    data: json,
                  })
                }
                data={note.data}
                placeholder="Describe this activity"
              />
            )}
          </Fragment>
        );
      })}

      {availableCategories.length > 0 && (
        <Styled.TeachingLearningSequenceListKebabWrapper>
          <DropdownSecondaryKebab
            buttonElement="Add"
            dropdownContents={
              <>
                {availableCategories.map((category, index) => {
                  return (
                    <DropdownItem
                      key={category.value}
                      onClick={() => onAdd(category.value)}
                    >
                      {category.label}
                    </DropdownItem>
                  );
                })}
              </>
            }
            aria-label="Click to add a note to this lesson's teaching and learning sequence"
            data-selector="add-teaching-note-activity-button"
          />
        </Styled.TeachingLearningSequenceListKebabWrapper>
      )}
    </Box>
  );
};

export default TeachingNotesListEdit;
